/**
 * Enquête n°26 — Adapté de l'Étude de Cas CCS n°25
 * (Spécificités du freinage en 2 roues.)
 */

import { Lightbulb, ShieldCheck, AlertTriangle, Quote, Users } from 'lucide-react';

export const freinageDeuxRouesEnquete = {
  slug: 'freinage-deux-roues',
  themeCode: 'V',
  issueNumber: 26,
  totalIssues: 26,
  series: 'enquetes',
  category: 'Enquête technique freinage',
  shortTitle: 'Freinage 2 roues',
  title: 'Frein avant ou arrière ? Le mythe qui te tue (ou te sauve)',
  kicker: 'Enquête n°26 · Série « Témoignages & Sécurité »',
  subtitle:
    'Mythe : « Le frein avant fait piquer du nez. » Vérité : il fournit 70 % du freinage. Mythe : « L’arrière est plus sûr. » Vérité : bloqué, il fait déraper. ABS, AFU, distance d’arrêt : la science du freinage en moto.',
  heroImage: 'https://images.unsplash.com/photo-1599360687378-5f8355df9bd5?w=1600&q=80&auto=format&fit=crop',
  heroImageAlt: 'Motard en blouson rouge — anticipation de freinage et distance de sécurité',
  readTimeMin: 7,
  seoDescription:
    'Enquête sur le freinage moto : rôle du frein avant (70 %) vs arrière (30 %), ABS obligatoire depuis 2015, AFU, distances de freinage, freinage d’urgence, freinage penché.',
  seoKeywords:
    'freinage moto, frein avant moto, frein arrière, ABS moto, AFU, distance freinage, freinage urgence, freinage penché',

  edito: {
    title: 'Le frein avant : ton meilleur ami mal aimé',
    body: 'La première leçon en moto-école : « Surtout, n’écrase pas le frein avant, tu vas tomber. » La première leçon en pilotage avancé : « Apprends à freiner FORT du frein avant. » Comment concilier les deux ? En comprenant la <strong>physique</strong> et en <strong>s’entraînant</strong>. 5 minutes de lecture qui peuvent te sauver la jambe (les blessures motos les plus graves après le crane).',
    signedBy: 'La rédaction Code 2 Roues',
  },

  sections: [
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 1',
      title: 'La répartition réelle : 70 % / 30 %',
      paragraphs: [
        '<strong>Sur le sec</strong> : la roue avant fournit environ <strong>70 % du freinage</strong>, l’arrière 30 %. Cette répartition vient du transfert de masse au freinage (poids qui se reporte sur l’avant).',
        '<strong>Sur le mouillé</strong> : la répartition se rapproche du 60/40 (l’adhérence diminue partout, mais le transfert reste).',
        '<strong>Au point mort</strong> (moto à l’arrêt sur béquille) : le frein arrière seul suffit à maintenir la moto immobile en pente.',
        '<strong>Mythe à déconstruire</strong> : « le frein arrière est plus sûr » — FAUX. Bloqué en virage, il fait déraper la moto qui glisse vers l’extérieur (« low-side »).',
      ],
      keyPoints: [
        'Avant : 70 % du freinage',
        'Arrière : 30 % du freinage',
        'Mouillé = 60/40',
        'Frein arrière bloqué = chute',
      ],
},
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 2',
      title: 'La technique du freinage d’urgence',
      paragraphs: [
        "<strong>Étape 1</strong> : redresse la moto AVANT de freiner si elle est penchée. Freiner penché = chute (perte d'adhérence sur roue avant inclinée).",
        '<strong>Étape 2</strong> : actionne les deux freins SIMULTANÉMENT, en commençant doucement, puis en augmentant la pression progressivement (sans à-coups).',
        '<strong>Étape 3</strong> : si tu as un ABS, tu peux écraser sans crainte de blocage. Si pas d’ABS, tu surveilles l’avant et tu relâches immédiatement si tu sens la roue glisser.',
        '<strong>Étape 4</strong> : garde le regard LOIN (là où tu veux aller), pas sur l’obstacle. Tu vas où tu regardes.',
      ],
      keyPoints: [
        'Redresser AVANT freiner',
        'Les 2 freins simultanément',
        'Pression progressive',
        'Regard loin',
      ],
},
    {
      type: 'didyouknow',
      icon: Lightbulb,
      title: "L’ABS moto : obligatoire depuis 2015, et il sauve réellement",
      body: "Le <strong>règlement européen UE 168/2013</strong> rend l’ABS obligatoire sur toute moto neuve ≥ 125 cm³ depuis le 1er janvier 2015. Résultat : selon une étude suédoise de 2018, l’ABS réduit de <strong>27 %</strong> les accidents corporels en moto. Ce n'est PAS un freinage plus court — c'est un freinage qui préserve la TRAJECTOIRE en empêchant le blocage. Sur gravier ou sol non stabilisé, l'efficacité chute. L'ABS n’est jamais une excuse pour rouler vite : il ne raccourcit pas la distance de freinage.",
    },
    {
      type: 'lesson',
      icon: AlertTriangle,
      partLabel: 'Chapitre 3',
      title: 'Distances de freinage : les vrais chiffres',
      paragraphs: [
        '<strong>À 50 km/h sur sec</strong> : 14 m de réaction + 14 m de freinage = <strong>28 m total</strong>.',
        '<strong>À 90 km/h sur sec</strong> : 25 m + 45 m = <strong>70 m total</strong>.',
        '<strong>À 130 km/h sur sec</strong> : 36 m + 96 m = <strong>132 m total</strong>.',
        "<strong>Règle d’or</strong> : doubler la vitesse quadruple la distance de freinage (l'énergie cinétique varie en V²). Sur le mouillé : multiplier par 1,5. Sur la neige : par 4.",
      ],
      keyPoints: [
        '50 km/h → 28 m',
        '90 km/h → 70 m',
        '130 km/h → 132 m',
        'Mouillé × 1,5',
      ],
},
    {
      type: 'tip',
      icon: Users,
      title: 'Tu n’as jamais fait de freinage d’urgence ?',
      body: 'Stage de <strong>perfectionnement à la conduite</strong> (CPM) : 250-400 € la journée en circuit sécurisé. Tu vas apprendre à écraser le frein avant sans peur. <strong>Statistique</strong> : un motard ayant fait un stage CPM réduit son risque d’accident de 40 % la 1ère année. Investissement majeur.',
    }
  ],
};

export default freinageDeuxRouesEnquete;
